import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { aiTokens, sparkleKeyframes } from './aiGeneratorTokens';
import { useWorkflowGenerator } from './useWorkflowGenerator';
import WorkflowConceptsGuide from './WorkflowConceptsGuide';
import { useTranslation } from '../../i18n/useTranslation';

const accentButtonSx = {
  background: aiTokens.accent,
  color: '#fff',
  fontWeight: 600,
  textTransform: 'none' as const,
  '&:hover': { background: aiTokens.accentHover },
};

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function AiGenerateDialog({ open, onClose }: Props) {
  const t = useTranslation();
  const generator = useWorkflowGenerator();
  const [text, setText] = useState('');

  const inFlight = generator.step === 'analyzing' || generator.step === 'requesting';

  const handleClose = () => {
    if (inFlight) return;
    setText('');
    generator.reset();
    onClose();
  };

  const handleSubmitDescription = () => {
    if (!text.trim()) return;
    void generator.submitDescription(text.trim());
    setText('');
  };

  const handleEdit = () => {
    setText('');
    generator.goToDescribe();
  };

  const isAnalyzeStep = generator.step === 'describing' || generator.step === 'clarifying';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: '14px' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.25, pr: 6 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '10px',
            background: aiTokens.accentSoft,
            color: aiTokens.accent,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 20 }} />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {t('workflow.ai.title')}
        </Typography>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ position: 'absolute', right: 10, top: 10 }}
          aria-label="close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <Box sx={{ height: 2, background: aiTokens.gradient }} />

      <DialogContent sx={{ pt: 3, pb: 1, minHeight: 280 }}>
        {generator.step === 'describing' && (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {t('workflow.ai.intro')}
            </Typography>
            <WorkflowConceptsGuide />
            <TextField
              label={t('workflow.ai.descriptionLabel')}
              placeholder={t('workflow.ai.descriptionPlaceholder')}
              value={text}
              onChange={(e) => setText(e.target.value)}
              multiline
              minRows={5}
              maxRows={12}
              fullWidth
              autoFocus
            />
          </Stack>
        )}

        {generator.step === 'analyzing' && (
          <CenteredSpinner label={t('workflow.ai.analyzing')} />
        )}

        {generator.step === 'clarifying' && (
          <Stack spacing={2}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {t('workflow.ai.needsMoreInfo')}
            </Typography>
            <Box component="ul" sx={{ pl: 3, m: 0, '& li': { mb: 0.5 } }}>
              {generator.questions.map((q) => (
                <li key={q}>
                  <Typography variant="body2" color="text.secondary">
                    {q}
                  </Typography>
                </li>
              ))}
            </Box>
            <TextField
              label={t('workflow.ai.answerLabel')}
              placeholder={t('workflow.ai.answerPlaceholder')}
              value={text}
              onChange={(e) => setText(e.target.value)}
              multiline
              minRows={3}
              maxRows={8}
              fullWidth
              autoFocus
            />
          </Stack>
        )}

        {generator.step === 'summary' && (
          <Stack spacing={2}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {t('workflow.ai.summaryTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('workflow.ai.summaryHintRequest')}
            </Typography>
            <Box
              sx={{
                p: 2,
                borderRadius: '10px',
                border: `1px solid ${aiTokens.accentBorder}`,
                background: aiTokens.accentSoft,
                whiteSpace: 'pre-wrap',
                fontSize: 14,
                lineHeight: 1.6,
              }}
            >
              {generator.summary}
            </Box>
          </Stack>
        )}

        {generator.step === 'requesting' && (
          <CenteredSpinner label={t('workflow.ai.requesting')} />
        )}

        {generator.step === 'done' && (
          <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                background: aiTokens.accentSoft,
                color: aiTokens.accent,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CheckCircleOutlineIcon sx={{ fontSize: 32 }} />
            </Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {t('workflow.ai.requestedTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 360 }}>
              {t('workflow.ai.requestedMessage')}
            </Typography>
          </Stack>
        )}

        {generator.step === 'error' && (
          <Box
            sx={{
              p: 2,
              borderRadius: '10px',
              border: `1px solid ${aiTokens.errorBorder}`,
              background: aiTokens.errorBg,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
            }}
          >
            <ErrorOutlineIcon sx={{ color: 'rgba(194,66,66,0.85)', mt: 0.25 }} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {t('workflow.ai.errorTitle')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {t('workflow.ai.errorBody')}
              </Typography>
              {generator.errorMessage && (
                <Typography
                  variant="caption"
                  sx={{ display: 'block', mt: 1, fontFamily: 'monospace', color: 'text.secondary' }}
                >
                  {generator.errorMessage}
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: 3, py: 2 }}>
        {isAnalyzeStep && (
          <Button
            variant="contained"
            disabled={!text.trim()}
            onClick={handleSubmitDescription}
            sx={accentButtonSx}
          >
            {t('workflow.ai.submit')}
          </Button>
        )}
        {generator.step === 'summary' && (
          <>
            <Button onClick={handleEdit} sx={{ textTransform: 'none' }}>
              {t('workflow.ai.edit')}
            </Button>
            <Button
              variant="contained"
              onClick={() => void generator.submitRequest()}
              sx={accentButtonSx}
            >
              {t('workflow.ai.requestButton')}
            </Button>
          </>
        )}
        {generator.step === 'done' && (
          <Button variant="contained" onClick={handleClose} sx={accentButtonSx}>
            {t('workflow.ai.close')}
          </Button>
        )}
        {generator.step === 'error' && (
          <Button onClick={handleEdit} sx={{ textTransform: 'none' }}>
            {t('workflow.ai.retry')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function CenteredSpinner({ label }: { label: string }) {
  return (
    <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
      <Box sx={{ position: 'relative', width: 56, height: 56, ...sparkleKeyframes }}>
        <CircularProgress size={56} thickness={3} sx={{ color: aiTokens.accent }} />
        <AutoAwesomeIcon
          sx={{
            position: 'absolute',
            inset: 0,
            margin: 'auto',
            fontSize: 24,
            color: aiTokens.accent,
            animation: 'sparkle 1.4s ease-in-out infinite',
          }}
        />
      </Box>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Stack>
  );
}
